import { nanoid } from 'nanoid';
import { compareManual } from './rank';
import { deriveTitle, type MarkdownDocument, type DocSessionState } from '@markbird/shared';
import { db, META_KEY } from './db';
import { metadataMirror, writeTags } from './metadata';
import { ensureLinkIndex, indexDocumentLinks, removeDocumentLinks } from './linkIndex';
import { ensureDocMeta, reprojectDoc } from './docMeta';

/**
 * Library write hook (phase B). The sync engine registers a listener so every
 * local mutation (create/save/rename/delete/restore/purge) is mirrored to
 * Firestore when signed in. `null` = no-op (local-only, the default).
 */
let writeListener: ((id: string) => void) | null = null;
export function setLibraryWriteListener(fn: ((id: string) => void) | null): void {
  writeListener = fn;
}
/**
 * Observers notified after any library mutation. The sync engine has its own
 * dedicated slot above; this is the general hook for derived state (the search
 * index) that has to follow writes without being on the write path.
 */
const observers = new Set<(id: string) => void>();

/** Subscribe to library mutations. Returns an unsubscribe function. */
export function onLibraryWrite(fn: (id: string) => void): () => void {
  observers.add(fn);
  return () => observers.delete(fn);
}

function notifyWrite(id: string): void {
  writeListener?.(id);
  refreshDerived(id);
}

/** Bring derived state (observers, the metadata projection) up to date with a write. */
function refreshDerived(id: string): void {
  for (const fn of observers) fn(id);
  // Every mutation already funnels through here, which makes it the one place a
  // new mutator cannot forget to refresh the metadata projection. Derived data,
  // so it is deliberately not awaited.
  void reprojectDoc(id);
}

/** Current authenticated owner id (set by the sync engine); null when signed out. */
let currentOwnerId: string | null = null;
export function setCurrentOwnerId(id: string | null): void {
  currentOwnerId = id;
}

/** Create a new document (owner = the signed-in user, or null when local-only). */
export async function createDocument(
  initial?: Partial<Pick<MarkdownDocument, 'title' | 'content' | 'createdAt'>>,
): Promise<MarkdownDocument> {
  const now = Date.now();
  const content = initial?.content ?? '';
  const doc: MarkdownDocument = {
    id: nanoid(),
    title: initial?.title ?? deriveTitle(content, 'Untitled'),
    content,
    // Imports from other apps carry the date the note was actually written;
    // everything else is new right now.
    createdAt: initial?.createdAt || now,
    updatedAt: now,
    ownerId: currentOwnerId,
    deletedAt: null,
    ...metadataMirror({ content, tags: [] }),
  };
  await db.documents.add(doc);
  await indexDocumentLinks(doc.id, content);
  notifyWrite(doc.id);
  return doc;
}

/** All documents incl. trashed (used by the sync engine). */
export async function listAllDocuments(): Promise<MarkdownDocument[]> {
  return db.documents.toArray();
}

/**
 * Upsert a document from a remote (Firestore) source. Skips the write listener,
 * which would push it straight back, but not derived state: without the
 * projection refresh a note synced from another device stayed out of the
 * library list until the projection was next rebuilt.
 */
export async function putRemoteDocument(doc: MarkdownDocument): Promise<void> {
  await db.documents.put(doc);
  await indexDocumentLinks(doc.id, doc.content);
  refreshDerived(doc.id);
}

/** Assign the signed-in owner to every previously-anonymous local document. */
export async function claimLocalDocuments(uid: string): Promise<void> {
  const all = await db.documents.toArray();
  for (const d of all) {
    if (d.ownerId == null) {
      await db.documents.update(d.id, { ownerId: uid });
      notifyWrite(d.id);
    }
  }
}

/** List live (non-trashed) documents: pinned first, then newest-first. */
export type LibrarySort = 'updated' | 'created' | 'title' | 'manual';

export async function listDocuments(sortBy: LibrarySort = 'updated'): Promise<MarkdownDocument[]> {
  const all = (await db.documents.toArray()).filter((d) => d.deletedAt == null);
  const cmp = (a: MarkdownDocument, b: MarkdownDocument): number => {
    switch (sortBy) {
      case 'created':
        return b.createdAt - a.createdAt;
      case 'title':
        return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      case 'manual':
        return compareManual(a, b);
      case 'updated':
      default:
        return b.updatedAt - a.updatedAt;
    }
  };
  // Pinned documents always sort first, then by the chosen order within each group.
  return all.sort((a, b) => Number(b.pinned ?? false) - Number(a.pinned ?? false) || cmp(a, b));
}

/** Pin/unpin a document (does not bump updatedAt, so order within a group holds). */
export async function setPinned(id: string, pinned: boolean): Promise<void> {
  await db.documents.update(id, { pinned, metaUpdatedAt: Date.now() });
  notifyWrite(id);
}

/** Move a document into a folder (null = root). Does not bump updatedAt. */
export async function setDocFolder(id: string, folder: string | null): Promise<void> {
  await db.documents.update(id, { folder, metaUpdatedAt: Date.now() });
  notifyWrite(id);
}

/** Set a document's place in the manual order. Does not bump updatedAt. */
export async function setDocRank(id: string, rank: string | null): Promise<void> {
  await db.documents.update(id, { rank, metaUpdatedAt: Date.now() });
  notifyWrite(id);
}

/** Rename a folder, updating every document under it (and its subfolders). */
export async function renameFolder(from: string, to: string | null): Promise<void> {
  const all = await db.documents.toArray();
  for (const d of all) {
    const f = d.folder;
    if (!f) continue;
    if (f === from || f.startsWith(`${from}/`)) {
      const suffix = f.slice(from.length); // '' or '/sub...'
      const next = to ? `${to}${suffix}` : suffix.replace(/^\//, '') || null;
      await db.documents.update(d.id, { folder: next, metaUpdatedAt: Date.now() });
      notifyWrite(d.id);
    }
  }
}

/** Remove a folder: move its documents (and subfolders' documents) to root. */
export async function removeFolder(path: string): Promise<void> {
  await renameFolder(path, null);
}

/**
 * Replace a document's tags, writing them into its frontmatter so they travel
 * with the file. Returns the new content; when the document is open in the
 * editor the caller must push it into the buffer, or the next autosave would
 * write the old frontmatter straight back over it.
 */
export async function setDocTags(id: string, tags: string[]): Promise<string | null> {
  const existing = await db.documents.get(id);
  if (!existing) return null;
  const content = writeTags(existing.content, tags);
  await db.documents.update(id, { content, tags, updatedAt: Date.now() });
  await indexDocumentLinks(id, content);
  notifyWrite(id);
  return content;
}

/** Update only the tag mirror, for callers that own the content write. */
export async function setDocTagsMirror(id: string, tags: string[]): Promise<void> {
  await db.documents.update(id, { tags });
  notifyWrite(id);
}

/** List trashed documents, most recently deleted first. */
export async function listTrash(): Promise<MarkdownDocument[]> {
  const all = await db.documents.toArray();
  return all
    .filter((d) => d.deletedAt != null)
    .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
}

export async function getDocument(id: string): Promise<MarkdownDocument | undefined> {
  return db.documents.get(id);
}

/**
 * Persist a content/title change and bump `updatedAt`. Title is re-derived from
 * the first H1 unless the caller supplies an explicit title (manual rename).
 */
export async function saveDocument(
  id: string,
  patch: { content?: string; title?: string },
): Promise<void> {
  const existing = await db.documents.get(id);
  if (!existing) return;
  const content = patch.content ?? existing.content;
  const title =
    patch.title !== undefined
      ? patch.title
      : patch.content !== undefined
        ? deriveTitle(content, existing.title || 'Untitled')
        : existing.title;
  // Re-mirror tags/aliases on every content write: the frontmatter the user
  // just edited is the truth, and queries must not have to parse markdown.
  const mirror = metadataMirror({ content, tags: existing.tags });
  await db.documents.update(id, { content, title, updatedAt: Date.now(), ...mirror });
  await indexDocumentLinks(id, content);
  notifyWrite(id);
}

export async function renameDocument(id: string, title: string): Promise<void> {
  await db.documents.update(id, { title: title.trim() || 'Untitled', updatedAt: Date.now() });
  notifyWrite(id);
}

export async function duplicateDocument(id: string): Promise<MarkdownDocument | undefined> {
  const src = await db.documents.get(id);
  if (!src) return undefined;
  return createDocument({ title: `${src.title} (copy)`, content: src.content });
}

/** Soft-delete: move a document to the trash (restorable). */
export async function deleteDocument(id: string): Promise<void> {
  await db.documents.update(id, { deletedAt: Date.now(), updatedAt: Date.now() });
  notifyWrite(id);
}

/** Restore a soft-deleted document from the trash. */
export async function restoreDocument(id: string): Promise<void> {
  await db.documents.update(id, { deletedAt: null, updatedAt: Date.now() });
  notifyWrite(id);
}

/** Permanently delete a document (and its session). */
export async function purgeDocument(id: string): Promise<void> {
  await db.transaction('rw', db.documents, db.sessions, db.snapshots, async () => {
    await db.documents.delete(id);
    await db.sessions.delete(id);
    await db.snapshots.where('docId').equals(id).delete();
  });
  await removeDocumentLinks(id);
  notifyWrite(id);
}

/** Permanently delete everything in the trash. */
export async function emptyTrash(): Promise<void> {
  const trashed = await listTrash();
  await db.transaction('rw', db.documents, db.sessions, db.snapshots, async () => {
    for (const d of trashed) {
      await db.documents.delete(d.id);
      await db.sessions.delete(d.id);
      await db.snapshots.where('docId').equals(d.id).delete();
    }
  });
  for (const d of trashed) notifyWrite(d.id);
}

/* ----------------------------- sessions ----------------------------- */

export async function saveSession(session: DocSessionState): Promise<void> {
  await db.sessions.put(session);
}

export async function getSession(docId: string): Promise<DocSessionState | undefined> {
  return db.sessions.get(docId);
}

/* ------------------------------- meta ------------------------------- */

export async function getLastOpenDocId(): Promise<string | null> {
  const meta = await db.meta.get(META_KEY);
  return meta?.lastOpenDocId ?? null;
}

export async function setLastOpenDocId(docId: string | null): Promise<void> {
  await db.meta.put({ key: META_KEY, lastOpenDocId: docId });
}

const WELCOME_CONTENT = `# Welcome to markbird

**Markdown. Stay in flow.** — a fast, minimalist editor that runs in your browser.

- Type on the left, see it render on the right.
- Press <kbd>Cmd/Ctrl</kbd> + <kbd>K</kbd> for the command palette, or <kbd>?</kbd> for all shortcuts.
- Paste or drop an image right into the editor.
- Open the outline (⌘/Ctrl-K → “outline”) to jump between headings.
- Everything works offline — your docs live in your browser.

## GitHub-Flavored Markdown

| Feature       | Supported |
| ------------- | :-------: |
| Tables        |    ✅     |
| Task lists    |    ✅     |
| Strikethrough |    ✅     |

- [x] Write markdown
- [ ] Share a read-only link

> [!NOTE]
> Nothing leaves your device until you explicitly share a document.

\`\`\`ts
const greeting = (name: string) => \`Hello, \${name}!\`;
\`\`\`
`;

/**
 * Ensure the library is non-empty on first run and return the document to open
 * (last open if still present, otherwise newest). Idempotent.
 */
export async function bootstrapLibrary(): Promise<MarkdownDocument> {
  // Cheap after the first run: a stored token short-circuits the rebuild.
  await Promise.all([ensureLinkIndex(), ensureDocMeta()]);
  const count = await db.documents.count();
  if (count === 0) {
    const doc = await createDocument({ content: WELCOME_CONTENT });
    await setLastOpenDocId(doc.id);
    return doc;
  }
  const lastId = await getLastOpenDocId();
  if (lastId) {
    const last = await db.documents.get(lastId);
    if (last) return last;
  }
  const [newest] = await listDocuments();
  // `newest` is defined because count > 0.
  return newest!;
}
